// frontend/src/pages/admin/TopicMergeModal.js
import { useState, useEffect, useMemo } from 'react';
import { useAuthFetch } from '../../hooks/useAuthFetch';

const FIELDS = [
  { key: 'name', label: 'Name' },
  { key: 'vendor', label: 'Vendor' },
  { key: 'examnumber', label: 'Exam #' },
  { key: 'expected_exam_questions', label: 'Expected Exam Questions' },
  { key: 'official_url', label: 'Official URL' },
  { key: 'lab_guide_viable', label: 'Lab Guide Viable' },
];

export default function TopicMergeModal({ sourceTopic, allTopics, preloadTargetId, onClose, onMerged }) {
  const { authFetch } = useAuthFetch();
  const [step, setStep] = useState(1); // 1: pick target, 2: resolve fields, 3: confirm
  const [targetId, setTargetId] = useState('');
  const [targetSearch, setTargetSearch] = useState('');
  const [preview, setPreview] = useState(null); // { source, target }
  const [resolvedFields, setResolvedFields] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Group topics by domain → group for the dropdown
  const groupedTopics = useMemo(() => {
    const q = targetSearch.toLowerCase();
    const candidates = allTopics.filter(t =>
      t.id !== sourceTopic.id &&
      (!q || t.name.toLowerCase().includes(q) || t.domain_name.toLowerCase().includes(q) || t.group_name.toLowerCase().includes(q))
    );
    return candidates.reduce((acc, t) => {
      const key = `${t.domain_name} › ${t.group_name}`;
      if (!acc[key]) acc[key] = [];
      acc[key].push(t);
      return acc;
    }, {});
  }, [allTopics, sourceTopic.id, targetSearch]);

  const fetchPreview = async (tId) => {
    setLoading(true);
    setError(null);
    try {
      const res = await authFetch(
        `${process.env.REACT_APP_API_URL}/api/admin/topics/merge/preview?sourceId=${sourceTopic.id}&targetId=${tId}`
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Preview failed');
      // Pre-select target values as defaults
      const defaults = {};
      FIELDS.forEach(f => { defaults[f.key] = data.target[f.key]; });
      setPreview(data);
      setResolvedFields(defaults);
      setStep(2);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Auto-trigger preview if preloadTargetId is set (from Find Duplicates)
  useEffect(() => {
    if (preloadTargetId) {
      setTargetId(String(preloadTargetId));
      fetchPreview(preloadTargetId);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleMerge = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authFetch(`${process.env.REACT_APP_API_URL}/api/admin/topics/merge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sourceId: sourceTopic.id, targetId: parseInt(targetId, 10), resolvedFields }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Merge failed');
      onMerged(sourceTopic.id, data); // data = updated target row
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBackdrop = (e) => { if (e.target === e.currentTarget) onClose(); };

  const overlayStyle = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  };
  const panelStyle = {
    background: 'var(--color-surface)', borderRadius: '12px', border: '1px solid var(--color-border)',
    padding: '1.5rem', width: '600px', maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto',
  };
  const inputStyle = {
    width: '100%', padding: '0.4rem 0.6rem', borderRadius: '6px',
    border: '1px solid var(--color-border-strong)', background: 'var(--color-surface)',
    color: 'var(--color-text)', fontSize: '0.875rem', boxSizing: 'border-box',
  };
  const labelStyle = { fontSize: '0.78rem', fontWeight: 600, color: 'var(--color-text-muted)', marginBottom: '0.25rem', display: 'block' };

  return (
    <div onClick={handleBackdrop} style={overlayStyle}>
      <div style={panelStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.25rem' }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: '1rem' }}>Merge Topic</div>
            <div style={{ fontSize: '0.78rem', color: 'var(--color-text-subtle)', marginTop: 2 }}>
              Source: <strong>{sourceTopic.name}</strong> ({sourceTopic.domain_name} › {sourceTopic.group_name})
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem', color: 'var(--color-text-muted)' }}>✕</button>
        </div>

        {error && <p style={{ color: 'var(--color-danger, red)', fontSize: '0.85rem', marginBottom: '1rem' }}>{error}</p>}

        {/* Step 1: Pick target */}
        {step === 1 && (
          <div>
            <label style={labelStyle}>Search for target topic</label>
            <input
              style={{ ...inputStyle, marginBottom: '0.75rem' }}
              placeholder="Search by name, domain, or group…"
              value={targetSearch}
              onChange={e => setTargetSearch(e.target.value)}
              autoFocus
            />
            <div style={{ maxHeight: 300, overflowY: 'auto', border: '1px solid var(--color-border)', borderRadius: 6 }}>
              {Object.entries(groupedTopics).map(([group, topics]) => (
                <div key={group}>
                  <div style={{ padding: '0.4rem 0.75rem', fontSize: '0.72rem', fontWeight: 700, color: 'var(--color-text-subtle)', background: 'var(--color-surface-alt, var(--color-surface))', borderBottom: '1px solid var(--color-border)' }}>
                    {group}
                  </div>
                  {topics.map(t => (
                    <div
                      key={t.id}
                      onClick={() => { setTargetId(String(t.id)); fetchPreview(t.id); }}
                      style={{ padding: '0.5rem 0.75rem', cursor: 'pointer', fontSize: '0.85rem', borderBottom: '1px solid var(--color-border)' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--color-accent-bg-2)'}
                      onMouseLeave={e => e.currentTarget.style.background = ''}
                    >
                      {t.name}
                    </div>
                  ))}
                </div>
              ))}
              {Object.keys(groupedTopics).length === 0 && (
                <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--color-text-subtle)', fontSize: '0.85rem' }}>No topics found</div>
              )}
            </div>
            {loading && <p style={{ marginTop: '0.75rem', fontSize: '0.85rem', color: 'var(--color-text-subtle)' }}>Loading preview…</p>}
          </div>
        )}

        {/* Step 2: Resolve fields */}
        {step === 2 && preview && (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.5rem', marginBottom: '0.5rem', fontSize: '0.72rem', fontWeight: 700, color: 'var(--color-text-subtle)' }}>
              <div>Field</div>
              <div style={{ color: 'var(--color-danger, #e55)' }}>Source (will be deleted)</div>
              <div style={{ color: 'var(--color-accent)' }}>Target (will survive)</div>
            </div>
            {FIELDS.map(f => {
              const srcVal = preview.source[f.key];
              const tgtVal = preview.target[f.key];
              const display = v => v === null || v === undefined || v === '' ? <em style={{ color: 'var(--color-text-subtle)' }}>—</em> : String(v);
              return (
                <div key={f.key} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.5rem', alignItems: 'center', padding: '0.4rem 0', borderBottom: '1px solid var(--color-border)' }}>
                  <div style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--color-text-muted)' }}>{f.label}</div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', fontSize: '0.82rem' }}>
                    <input type="radio" name={f.key} checked={resolvedFields[f.key] === srcVal} onChange={() => setResolvedFields(p => ({ ...p, [f.key]: srcVal }))} />
                    {display(srcVal)}
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', fontSize: '0.82rem' }}>
                    <input type="radio" name={f.key} checked={resolvedFields[f.key] === tgtVal} onChange={() => setResolvedFields(p => ({ ...p, [f.key]: tgtVal }))} />
                    {display(tgtVal)}
                  </label>
                </div>
              );
            })}
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
              <button onClick={() => setStep(1)} className="costs-range-btn">Back</button>
              <button
                onClick={() => setStep(3)}
                style={{ padding: '0.3rem 1rem', borderRadius: '6px', border: '1px solid rgba(249,115,22,0.55)', background: 'var(--color-accent-bg-2)', color: 'var(--color-text)', fontSize: '0.85rem', fontWeight: 500, cursor: 'pointer' }}
              >
                Review &amp; Confirm
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Confirm */}
        {step === 3 && preview && (
          <div>
            <p style={{ fontSize: '0.9rem', marginBottom: '1rem' }}>
              <strong>{preview.source.question_count} questions</strong> and{' '}
              <strong>{preview.source.goal_count} goals</strong> will be moved to{' '}
              <strong>{resolvedFields.name || preview.target.name}</strong>.{' '}
              <strong>{preview.source.name}</strong> will be soft-deleted.
            </p>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button onClick={() => setStep(2)} className="costs-range-btn" disabled={loading}>Back</button>
              <button
                onClick={handleMerge}
                disabled={loading}
                style={{ padding: '0.3rem 1rem', borderRadius: '6px', border: '1px solid rgba(220,38,38,0.5)', background: 'rgba(220,38,38,0.12)', color: 'var(--color-text)', fontSize: '0.85rem', fontWeight: 500, cursor: loading ? 'wait' : 'pointer' }}
              >
                {loading ? 'Merging…' : 'Confirm Merge'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
